import React from "react"
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack
} from "@carrotfertility/carotene-core"
import { Form, FormRadioGroup, FormTextField, useWatch } from "@carrotfertility/carotene-core-x"
import { FormattedMessage, useIntl } from "react-intl"
import { useFormState } from "react-hook-form"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { emailValidationCaroteneCore } from "#/utils/Regexes"
import { useIntlValidator } from "#/utils/hooks/useIntlValidator"

type EmailOption = "member" | "partner" | "another" | ""

interface WhichEmailStepProps {
  email: string
  onContinue: (email: string) => void
}

function WhichEmailStepFormContent(): JSX.Element {
  const intl = useIntl()
  const { preferredEmail, partnerEmail } = useCurrentUser()
  const intlEmailValidation = useIntlValidator(emailValidationCaroteneCore)
  const emailOption = useWatch({ name: "emailOption" }) as EmailOption
  const { isValid } = useFormState()

  const options = [
    ...(preferredEmail ? [{ value: "member", label: preferredEmail }] : []),
    ...(partnerEmail ? [{ value: "partner", label: partnerEmail }] : []),
    { value: "another", label: intl.formatMessage({ defaultMessage: "Another email" }) }
  ]

  return (
    <>
      <DialogContent>
        <DialogContentText>
          <FormattedMessage defaultMessage="You'll be able to update it when you create your account." />
        </DialogContentText>
        <Stack
          sx={(theme) => ({
            rowGap: theme.spacing(theme.tokens.spacing.md)
          })}
        >
          <FormRadioGroup
            name="emailOption"
            options={options}
            controllerProps={{
              rules: {
                required: intl.formatMessage({ defaultMessage: "Required" })
              }
            }}
          />
          {emailOption === "another" && (
            <FormTextField
              name="anotherEmail"
              type="email"
              label={intl.formatMessage({ defaultMessage: "Email" })}
              placeholder={intl.formatMessage({ defaultMessage: "Enter another email" })}
              registerOptions={{
                required: intl.formatMessage({ defaultMessage: "Required" }),
                validate: (value) => intlEmailValidation(value)
              }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" type="submit" disabled={!emailOption || !isValid}>
          <FormattedMessage defaultMessage="Continue" />
        </Button>
      </DialogActions>
    </>
  )
}

export function WhichEmailStep({ email, onContinue }: WhichEmailStepProps): JSX.Element {
  const { preferredEmail, partnerEmail } = useCurrentUser()

  const getDefaultEmailOption = (): EmailOption => {
    if (!email) return ""
    if (email === preferredEmail) return "member"
    if (email === partnerEmail) return "partner"
    return "another"
  }

  const defaultEmailOption = getDefaultEmailOption()

  const handleSubmit = (values: { emailOption: EmailOption; anotherEmail: string }) => {
    const emailByOption: Record<string, string | undefined> = {
      member: preferredEmail,
      partner: partnerEmail,
      another: values.anotherEmail
    }
    onContinue(emailByOption[values.emailOption] ?? "")
  }

  return (
    <>
      <DialogTitle>
        <FormattedMessage defaultMessage="Which email should we use to get you started with Blueberry?" />
      </DialogTitle>
      <Form
        onSubmit={handleSubmit}
        defaultValues={{
          emailOption: defaultEmailOption,
          anotherEmail: defaultEmailOption === "another" ? email : ""
        }}
      >
        <WhichEmailStepFormContent />
      </Form>
    </>
  )
}
